import { useState } from 'react';
import { rankItems } from '@/mocks/home';
import SectionHeading from './SectionHeading';

const trendIcon: Record<string, string> = {
  up: 'ri-arrow-up-line',
  flat: 'ri-subtract-line',
  down: 'ri-arrow-down-line',
};

const trendColor: Record<string, string> = {
  up: 'text-primary-600',
  flat: 'text-foreground-400',
  down: 'text-secondary-600',
};

export default function RankingBoard() {
  const [range, setRange] = useState('今日');
  const ranges = ['今日', '本周', '本月'];

  return (
    <section id="ranking" className="w-full border-b border-background-200/70 bg-background-100/60">
      <div className="mx-auto w-full max-w-7xl px-4 py-14 md:px-8 md:py-20">
        <SectionHeading
          index="02"
          label="Hot Ranking"
          title="今日热度榜 · 话题实时排位"
          desc="按站内浏览、讨论与分享综合计算热度，每十分钟刷新一次，名次变化会同步标注升降趋势，方便快速判断近期看点。"
          extra={
            <div className="inline-flex items-center gap-1 rounded-full border border-background-200 bg-background-50 p-1">
              {ranges.map((item) => (
                <button
                  key={item}
                  type="button"
                  onClick={() => setRange(item)}
                  className={
                    range === item
                      ? 'cursor-pointer whitespace-nowrap rounded-full bg-foreground-900 px-4 py-2 text-xs font-label font-medium text-background-50'
                      : 'cursor-pointer whitespace-nowrap rounded-full px-4 py-2 text-xs font-label text-foreground-600 transition-colors hover:text-primary-600'
                  }
                >
                  {item}
                </button>
              ))}
            </div>
          }
        />

        <div className="mt-8 grid grid-cols-1 gap-6 lg:grid-cols-12">
          <div className="lg:col-span-8">
            <ol className="overflow-hidden rounded-lg border border-background-200 bg-background-50">
              {rankItems.map((item) => {
                const isTop = item.rank <= 3;
                return (
                  <li
                    key={item.id}
                    className="flex items-center gap-4 border-b border-background-200/70 px-4 py-4 transition-colors last:border-b-0 hover:bg-background-100/70 md:px-5"
                  >
                    <span
                      className={
                        item.rank === 1
                          ? 'flex h-9 w-9 shrink-0 items-center justify-center rounded-md bg-primary-500 font-heading text-base font-bold text-background-50'
                          : item.rank === 2
                            ? 'flex h-9 w-9 shrink-0 items-center justify-center rounded-md bg-accent-500 font-heading text-base font-bold text-accent-950'
                            : item.rank === 3
                              ? 'flex h-9 w-9 shrink-0 items-center justify-center rounded-md bg-secondary-500 font-heading text-base font-bold text-background-50'
                              : 'flex h-9 w-9 shrink-0 items-center justify-center rounded-md border border-background-200 bg-background-100 font-heading text-base font-bold text-foreground-500'
                      }
                    >
                      {item.rank}
                    </span>

                    <div className="min-w-0 flex-1">
                      <div className="flex flex-wrap items-center gap-2">
                        <a
                          href="#feed"
                          className="truncate text-sm font-label font-medium text-foreground-950 transition-colors hover:text-primary-600 md:text-base"
                        >
                          {item.topic}
                        </a>
                        {isTop && (
                          <span className="rounded-full bg-primary-100 px-2 py-0.5 text-[11px] font-label text-primary-700">
                            {item.tag}
                          </span>
                        )}
                      </div>
                      <div className="mt-2 h-1.5 w-full max-w-xs overflow-hidden rounded-full bg-background-200">
                        <span
                          className="block h-full rounded-full bg-primary-400/80"
                          style={{ width: `${Number(item.heat)}%` }}
                        ></span>
                      </div>
                    </div>

                    <div className="flex shrink-0 items-center gap-3">
                      <div className="text-right">
                        <p className="font-heading text-base font-bold text-foreground-950">{item.heat}</p>
                        <p className="text-[11px] font-label text-foreground-500">热度值</p>
                      </div>
                      <span className={`w-4 h-4 flex items-center justify-center ${trendColor[item.trend]}`}>
                        <i className={`${trendIcon[item.trend]} text-base`} aria-hidden="true"></i>
                      </span>
                    </div>
                  </li>
                );
              })}
            </ol>
          </div>

          <div className="lg:col-span-4">
            <div className="rounded-lg border border-primary-200 bg-primary-50 p-5 md:p-6">
              <div className="flex items-center justify-between">
                <span className="inline-flex items-center gap-2 rounded-full bg-primary-500 px-3 py-1 text-[11px] font-label text-background-50">
                  <span className="w-3.5 h-3.5 flex items-center justify-center">
                    <i className="ri-fire-fill text-xs" aria-hidden="true"></i>
                  </span>
                  今日焦点
                </span>
                <span className="text-[11px] font-label text-primary-700">TOP 1</span>
              </div>

              <h3 className="mt-4 font-heading text-lg font-bold leading-snug text-foreground-950 md:text-xl">
                新一季综艺阵容路透整理
              </h3>
              <p className="mt-3 text-sm leading-7 text-foreground-700">
                今日热度最高的话题，讨论量在午后明显攀升，相关图集与现场细节可在爆料速览区查看完整版本。
              </p>

              <div className="mt-5 grid grid-cols-3 gap-3 border-t border-primary-200 pt-5">
                <div>
                  <p className="font-heading text-lg font-bold text-primary-700">98.6</p>
                  <p className="mt-0.5 text-[11px] font-label text-foreground-600">热度值</p>
                </div>
                <div>
                  <p className="font-heading text-lg font-bold text-primary-700">+12%</p>
                  <p className="mt-0.5 text-[11px] font-label text-foreground-600">日环比</p>
                </div>
                <div>
                  <p className="font-heading text-lg font-bold text-primary-700">4.2 万</p>
                  <p className="mt-0.5 text-[11px] font-label text-foreground-600">讨论数</p>
                </div>
              </div>

              <a
                href="#feed"
                className="mt-6 inline-flex w-full cursor-pointer items-center justify-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-5 py-3 text-sm font-label font-medium text-background-50 transition-colors hover:bg-primary-600"
              >
                查看话题详情
                <span className="w-4 h-4 flex items-center justify-center">
                  <i className="ri-arrow-right-line text-base" aria-hidden="true"></i>
                </span>
              </a>
            </div>

            <div className="mt-4 rounded-lg border border-background-200 bg-background-50 p-5">
              <p className="text-sm font-label font-medium text-foreground-950">榜单说明</p>
              <ul className="mt-3 space-y-2.5">
                {['热度值由浏览、讨论、分享三项加权得出', '每十分钟刷新一次，名次变化实时标注', '同人重复访问不计入热度统计'].map(
                  (text) => (
                    <li key={text} className="flex items-start gap-2 text-xs leading-6 text-foreground-600">
                      <span className="mt-0.5 w-3.5 h-3.5 flex items-center justify-center text-secondary-600">
                        <i className="ri-checkbox-blank-circle-fill text-[6px]" aria-hidden="true"></i>
                      </span>
                      {text}
                    </li>
                  ),
                )}
              </ul>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}